import { FC, ReactElement } from 'react'
import cn from 'classnames'
import { LoadingOutlined } from '@ant-design/icons'
import DatePicker from '@pages/Admin/CRM/components/base/DatePicker/DatePicker'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import Preloader from '@UI/Preloader'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import { usePlatformTimeZone } from '@/hooks/usePlatformTimeZone'
import {
  buildTaskCardSelectStickerOptions,
  formatTaskTrackerStickerDateTimeForDisplay,
  formatTaskTrackerStickerDateForApi,
  parseTaskTrackerStickerDateValue,
} from '../../../../../lib/taskTrackerTaskStickers.utils'
import {
  isTaskTrackerDateLikeStickerFieldType,
  isTaskTrackerDateTimeLikeStickerFieldType,
  isTaskTrackerSystemDatetimeStickerFieldType,
} from '../../../../../lib/taskTrackerBoard.stickerFilters.utils'
import {
  formatTaskTrackerUtcPlatformDatetimeForPersist,
  parseTaskTrackerSystemDatetimeFilterValue,
} from '../../../../../lib/taskTrackerSystemDatetime.utils'
import TaskStickerEditableValue from './TaskStickerEditableValue'
import TaskStickerCrmLeadValue from './TaskStickerCrmLeadValue'
import TaskTrackerPermissionTooltip from '../../../../TaskTrackerPermissionTooltip/TaskTrackerPermissionTooltip'
import { isTaskTrackerCrmLeadSticker } from '../../../../../lib/taskTrackerCrmLeadSticker.utils'
import classes from './index.module.css'
import type { StickerFieldRow } from './useTaskStickerFieldsDraft'

const LoadingIcon = LoadingOutlined as unknown as FC<{
  spin?: boolean
  className?: string
}>

type TaskStickerFieldsSectionProps = {
  fieldRows: StickerFieldRow[]
  loading: boolean
  error: string | null
  performerOptions?: Array<{ value: string; label: string; title: string }>
  performerOptionsLoading?: boolean
  performerOptionsError?: string | null
  savingPerformers?: boolean
  isFieldSaving: (fieldSettingId: number) => boolean
  readOnly?: boolean
  readOnlyReason?: string | null
  onSaveValue: (
    fieldSettingId: number,
    value: string | null,
    options?: {
      displayValue?: string | null
      crmLeadId?: number | null
      crmFunnelId?: number | null
    },
  ) => void | Promise<boolean | void>
  onSavePerformers?: (value: string | string[] | null) => void | Promise<void>
}

const buildSelectOptions = (row: StickerFieldRow) =>
  buildTaskCardSelectStickerOptions(
    row.options,
    typeof row.value === 'string' ? row.value : null,
  )

const TaskStickerFieldsSection: FC<TaskStickerFieldsSectionProps> = ({
  fieldRows,
  loading,
  error,
  performerOptions = [],
  performerOptionsLoading = false,
  performerOptionsError = null,
  savingPerformers = false,
  isFieldSaving,
  readOnly = false,
  readOnlyReason = null,
  onSaveValue,
  onSavePerformers,
}) => {
  const platformTimeZone = usePlatformTimeZone()

  const wrapReadOnlyControl = (control: ReactElement) => {
    if (!readOnly || !readOnlyReason) return control

    return (
      <TaskTrackerPermissionTooltip title={readOnlyReason} block>
        {control}
      </TaskTrackerPermissionTooltip>
    )
  }

  const renderFieldControl = (row: StickerFieldRow) => {
    const saving = isFieldSaving(row.fieldSettingId)
    const isControlDisabled = readOnly || saving
    const isPerformerControlDisabled =
      readOnly || savingPerformers || !onSavePerformers

    if (
      isTaskTrackerSystemDatetimeStickerFieldType(row.fieldType, row.systemKey)
    ) {
      const value =
        typeof row.value === 'string'
          ? formatTaskTrackerStickerDateTimeForDisplay(
              row.value,
              platformTimeZone,
            )
          : '—'

      return (
        <div
          className={cn(classes.readOnlySystemValue, {
            [classes.readOnlySystemValueEmpty]: value === '—',
          })}
        >
          {value}
        </div>
      )
    }

    if (row.fieldType === 'performer') {
      const performerValue = Array.isArray(row.value) ? row.value : []

      return wrapReadOnlyControl(
        <SelectField
          mode="multiple"
          className={cn(iclasses.newSelectMultiple, classes.selectField, {
            [classes.selectFieldLoading]: savingPerformers,
          })}
          wrapperClassName={classes.selectFieldWrapper}
          popupClassName={cn(iclasses.newSelectPopup, classes.selectFieldPopup)}
          value={performerValue}
          options={performerOptions}
          optionLabelProp="title"
          placeholder="Не назначены"
          allowClear
          showSearch
          size="small"
          loading={performerOptionsLoading || savingPerformers}
          disabled={isPerformerControlDisabled}
          notFoundContent={
            performerOptionsError
              ? performerOptionsError
              : performerOptionsLoading
                ? 'Загрузка...'
                : 'Сотрудники с доступом к доске не найдены'
          }
          getPopupContainer={() => document.body}
          onChange={(value) => void onSavePerformers?.(value)}
        />,
      )
    }

    if (isTaskTrackerDateLikeStickerFieldType(row.fieldType)) {
      const withTime = isTaskTrackerDateTimeLikeStickerFieldType(row.fieldType)
      const rawValue = typeof row.value === 'string' ? row.value : null
      const dateValue = withTime
        ? parseTaskTrackerSystemDatetimeFilterValue(
            rawValue,
            platformTimeZone,
            true,
          )
        : parseTaskTrackerStickerDateValue(rawValue)

      return wrapReadOnlyControl(
        <div
          className={cn(classes.datePickerWrapper, {
            [classes.datePickerWrapperLoading]: saving,
          })}
        >
          <DatePicker
            size="small"
            className={classes.datePicker}
            format={withTime ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY'}
            showTime={withTime ? { format: 'HH:mm' } : undefined}
            placeholder="Не указана"
            allowClear={!isControlDisabled}
            inputReadOnly
            value={dateValue}
            disabled={isControlDisabled}
            suffixIcon={
              saving ? (
                <LoadingIcon spin className={classes.datePickerLoadingIcon} />
              ) : undefined
            }
            getPopupContainer={() => document.body}
            onChange={(_date, dateString) => {
              if (readOnly) return

              const rawDateString = Array.isArray(dateString)
                ? dateString[0]
                : dateString
              const nextValue = withTime
                ? formatTaskTrackerUtcPlatformDatetimeForPersist(
                    rawDateString,
                    platformTimeZone,
                  )
                : formatTaskTrackerStickerDateForApi(rawDateString)

              void onSaveValue(row.fieldSettingId, nextValue)
            }}
          />
        </div>,
      )
    }

    if (row.fieldType === 'select') {
      const selectOptions = buildSelectOptions(row)
      const selectValue =
        typeof row.value === 'string' ? row.value.trim() || null : null

      return wrapReadOnlyControl(
        <SelectField
          className={cn(iclasses.newSelect, classes.selectField, {
            [classes.selectFieldLoading]: saving,
          })}
          wrapperClassName={classes.selectFieldWrapper}
          popupClassName={cn(iclasses.newSelectPopup, classes.selectFieldPopup)}
          value={selectValue}
          options={selectOptions}
          optionLabelProp="title"
          showOptionColors
          placeholder="Не выбрано"
          allowClear
          showSearch
          size="small"
          loading={saving}
          disabled={isControlDisabled || selectOptions.length === 0}
          notFoundContent="Нет вариантов"
          getPopupContainer={() => document.body}
          onChange={(value) => {
            if (readOnly) return
            if (Array.isArray(value)) return

            void onSaveValue(
              row.fieldSettingId,
              value != null && value !== '' ? String(value) : null,
            )
          }}
        />,
      )
    }

    if (isTaskTrackerCrmLeadSticker(row.fieldType, row.systemKey)) {
      return wrapReadOnlyControl(
        <TaskStickerCrmLeadValue
          fieldSettingId={row.fieldSettingId}
          value={typeof row.value === 'string' ? row.value : null}
          crmLeadId={row.crmLeadId}
          crmFunnelId={row.crmFunnelId}
          saving={saving}
          disabled={readOnly}
          onSave={onSaveValue}
        />,
      )
    }

    if (row.fieldType === 'number') {
      return wrapReadOnlyControl(
        <TaskStickerEditableValue
          fieldSettingId={row.fieldSettingId}
          fieldType="number"
          value={typeof row.value === 'string' ? row.value : null}
          saving={saving}
          disabled={readOnly}
          onSave={onSaveValue}
        />,
      )
    }

    return wrapReadOnlyControl(
      <TaskStickerEditableValue
        fieldSettingId={row.fieldSettingId}
        fieldType="text"
        value={typeof row.value === 'string' ? row.value : null}
        saving={saving}
        disabled={readOnly}
        onSave={onSaveValue}
      />,
    )
  }

  if (!loading && !error && fieldRows.length === 0) {
    return null
  }

  return (
    <section className={classes.root}>
      <h3 className={classes.title}>Стикеры</h3>

      {loading ? (
        <div className={classes.state} aria-busy="true">
          <Preloader />
        </div>
      ) : null}

      {!loading && error ? <p className={classes.error}>{error}</p> : null}

      {!loading && !error && fieldRows.length > 0 ? (
        <div className={classes.list}>
          {fieldRows.map((row, index) => (
            <div key={row.fieldSettingId}>
              {index > 0 ? <div className={classes.divider} /> : null}
              <div className={classes.row}>
                <span
                  className={classes.colorMarker}
                  style={{ backgroundColor: row.color || '#9747ff' }}
                  aria-hidden
                />
                <div className={classes.rowBody}>
                  <span className={classes.rowLabel}>{row.title}</span>
                  {renderFieldControl(row)}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  )
}

export default TaskStickerFieldsSection
